// src/hooks/useChat.ts

import { useState, useCallback, useRef } from 'react';
import axios from 'axios';

// Importaciones desde los nuevos archivos
// ELIMINAR HistoryChunk de la importación
import { Msg, ApiResponse, LatestToolCallRequest } from '../types/chatTypes';
import { mapHistoryToChunks } from '../utils/chatUtils';

// Ya no necesitamos definir interfaces o la función utilitaria aquí
// Tampoco la constante SYSTEM_PROMPT

export function useChat(apiBaseUrl: string) {
    const [history, setHistory] = useState<Msg[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const messageIdCounter = useRef(0);
    const [latestToolCallRequest, setLatestToolCallRequest] = useState<LatestToolCallRequest | null>(null);

    const addMessage = useCallback((message: Omit<Msg, 'id'>) => {
        const newId = `${message.from}-${messageIdCounter.current++}-${Date.now()}`;
        const messageWithId = { ...message, id: newId };
        setHistory(prev => [...prev, messageWithId]);
        return messageWithId;
    }, []);

    const sendMessage = useCallback(async (userText: string) => {
        if (!userText.trim() || isLoading) return;
        addMessage({ from: 'user', text: userText });
        setIsLoading(true);
        setError(null);
        setLatestToolCallRequest(null);

        try {
            const contextChunks = mapHistoryToChunks(history.slice(-16));
            const response = await axios.post<ApiResponse>(
                `${apiBaseUrl}/api/chat`, // <--- CORREGIDO
                { message: userText, chunks: contextChunks }
            );

            const botResponseText = response.data.message;
            const toolCalls = response.data.toolCalls;

            if (botResponseText) {
                addMessage({ from: 'bot', text: botResponseText });
            } else if (toolCalls && toolCalls.length > 0) {
                const firstToolCall = toolCalls[0];
                addMessage({ from: 'bot', text: `(Recibida solicitud para ${firstToolCall?.function?.name || 'acción'}...)` });
                setLatestToolCallRequest(firstToolCall);
            } else {
                setError("Respuesta inesperada del servidor.");
                addMessage({ from: 'error', text: "Respuesta inesperada del servidor." });
            }
        } catch (err: any) {
            const errorMsg = err.response?.data?.error || err.message || 'Error de conexión.';
            setError(errorMsg);
            addMessage({ from: 'error', text: `Error: ${errorMsg}` });
        } finally {
            setIsLoading(false);
        }
    }, [isLoading, history, apiBaseUrl, addMessage]);

    // --- Envío de resultado de herramienta (payload explícito para el backend) ---
    const sendToolResult = useCallback(async (toolCallId: string, resultContent: string) => {
        setIsLoading(true);
        setError(null);

        const historyChunks = mapHistoryToChunks(history);

        try {
            const response = await axios.post<ApiResponse>(
                `${apiBaseUrl}/api/chat`, // <--- CORREGIDO
                {
                    type: 'tool_result', // Indicamos al backend que es un resultado de herramienta
                    toolCallId: toolCallId,
                    resultContent: resultContent,
                    chunks: historyChunks
                }
            );

            if (response.data.message) {
                addMessage({ from: 'bot', text: response.data.message });
            } else if (response.data.toolCalls && response.data.toolCalls.length > 0) {
                const firstToolCall = response.data.toolCalls[0];
                addMessage({ from: 'bot', text: `(Recibida nueva solicitud para ${firstToolCall?.function?.name || 'acción'}...)` });
                console.warn("Recibido un tool call inmediatamente después de enviar un resultado. Asegúrate de manejar esto correctamente para evitar bucles.");
                setLatestToolCallRequest(firstToolCall);
            } else if (response.data.error) {
                setError(response.data.error);
                addMessage({ from: 'error', text: `Error del servidor al procesar resultado: ${response.data.error}` });
            } else {
                setError("Respuesta inesperada del servidor tras procesar acción.");
                addMessage({ from: 'error', text: "Respuesta inesperada del servidor." });
            }

        } catch (err: any) {
            const errorMsg = err.response?.data?.error || err.message || 'Error desconocido al enviar resultado.';
            const detailedError = err.response?.data ? `Detalles: ${JSON.stringify(err.response.data)}` : '';
            setError(`Error al enviar resultado: ${errorMsg} ${detailedError}`);
            addMessage({ from: 'error', text: `Error al enviar resultado: ${errorMsg}` });
            console.error("Error sending tool result:", err.response?.data || err.message || err);
        } finally {
            setIsLoading(false);
            // La lógica de limpiar latestToolCallRequest se maneja en el useEffect de ChatWindow/useToolCallProcessor
        }
    }, [apiBaseUrl, addMessage, history]);

    const clearToolRequest = useCallback(() => {
        setLatestToolCallRequest(null);
    }, []);

    return {
        history,
        isLoading,
        error,
        sendMessage,
        latestToolCallRequest,
        clearToolRequest,
        sendToolResult,
    };
}